import React from 'react';
import { FormGroup, Row, Col } from 'reactstrap';
import { isEqual } from 'lodash-es';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm, FlexCenterRow, FormCheckbox, FormDivider, onFieldChange, onReactSelectChanged, toasty,
    Input,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, { ApiRoutes, AppNavPaths, SiteRoles } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { SaveButton } from '../common/forms/SaveButton';
import { Group } from './Group';

class GroupForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            group: new Group(),
            roles: [],
            dispatchCompanies: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            this.populateState();
        }
    }

    onChange = onFieldChange;

    onClearErrors = () => this.setState({ errors: {} });

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async () => {
        const { group } = this.state;
        const response = await httpClient.delete(ApiRoutes.company.delete(group.id)).catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    onSelectChanged = onReactSelectChanged;

    onSubmit = async () => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        const { group } = this.state;

        const cloned = clone(group);

        if (this.props.location.pathname == AppNavPaths.GroupNew) {
            await this.createGroup(cloned);
        } else {
            await this.updateGroup(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    addAllDispatching = () => {
        const { group, dispatchCompanies } = { ...this.state };
        group.expandedDispatchCompanies = [...dispatchCompanies.map((x) => x.value)];
        this.setState({ group });
    };

    createGroup = async (group) => {
        const result = await httpClient
            .post(ApiRoutes.group.create(), group)
            .catch(this.handleSaveError);
        if (result) {
            toasty.success('Group created.');
            this.context.history.push(`${AppNavPaths.Group}/${result.data}`);
        } else {
            this.setState({ saving: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const groupId = this.props.match.params.id;

        const [roles, group, dispatchLocs] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.roles()),
            groupId ? httpClient.js(ApiRoutes.group.byId(groupId)) : new Group(),
            httpClient.js(ApiRoutes.company.dispatchCompanies()),
        ]);

        const dispatchLocsMapped = (dispatchLocs ?? []).map((x) => ({ label: x.companyName, value: x.id }));

        const originalData = clone(group);

        this.setState({
            originalData,
            group,
            loading: false,
            roles,
            saving: false,
            dispatchCompanies: dispatchLocsMapped,
        });
    }

    removeAllDispatching = () => {
        const { group } = { ...this.state };
        group.expandedDispatchCompanies = [];
        this.setState({ group });
    };

    resetForm = () => this.setState({ formValidated: false });

    updateGroup = async (group) => {
        const result = await httpClient
            .put(ApiRoutes.group.byId(group.id), group)
            .catch(this.handleSaveError);

        if (result) {
            this.populateState();
            toasty.success('Group saved.');
            this.context.history.replace(this.props.location.pathname);
        } else {
            this.setState({ saving: false });
        }
    };

    render() {
        const {
            group,
            originalData,
            errors,
            formValidated,
            roles,
            saving,
            dispatchCompanies,
        } = this.state;

        const { tenant } = this.context;
        const { tenantSettings } = tenant;

        const existing = !!group.id;

        const selectedDispatchCompanies = (dispatchCompanies ?? []).filter((x) => (group.expandedDispatchCompanies ?? []).includes(x.value));
        const groupRoles = (group?.userRoles ?? []);

        if (selectedDispatchCompanies.length) selectedDispatchCompanies.sort((a, b) => (a.label > b.label ? 1 : -1));

        // If admin or regional support is selected as a role for this group, there is no need to show
        // the dispatching dropdown, as these roles get all dispatching companies by default.
        const adminOrRegSpptSelected = groupRoles.includes(SiteRoles.Administrator)
            || groupRoles.includes(SiteRoles.RegionalSupport);

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, group)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    backUrl={AppNavPaths.Groups}
                    backLabel="Back to Groups"
                    formShown={this.context.formIsOpen}
                    formId="groupForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={!group.id ? 'New Group' : 'Edit Group'}
                    formName="groupForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={this.state.loading}
                >

                    <Row className="pt-3">
                        <Col
                            xl="6"
                            lg="8"
                            md="10"
                            sm="12"
                            className="ml-auto mr-auto"
                        >
                            <Input
                                label="Name"
                                id="groupName"
                                name="group.groupName"
                                value={group.groupName ?? ''}
                                onChange={this.onChange}
                                placeholder="Enter Name (max 150 characters)"
                                maxLength="150"
                                pattern="[^()/><\][\\\x22,;|]+"
                                error="Name is required and can only contain the following special characters: hyphens and periods."
                                required
                            />

                            <ValidatedSelect
                                id="userRoles"
                                name="group.userRoles"
                                label="Roles"
                                required
                                isMulti
                                options={roles}
                                value={(roles ?? []).filter((x) => (group.userRoles ?? []).includes(x.value)) ?? ''}
                                onChange={this.onSelectChanged}
                                error="A group role is required."
                            />

                            <Input
                                label="Code"
                                id="code"
                                name="group.code"
                                value={group.code ?? ''}
                                onChange={this.onChange}
                                placeholder="Enter Code (max 15 characters)"
                                required={Boolean(tenantSettings.quirksEnabled)}
                                maxLength="15"
                                error="Name is required and can only contain the following special characters: hyphens and periods."
                            />
                            <FormGroup>
                                <FormCheckbox
                                    className="mt-2 mb-2"
                                    id="isFieldGroup"
                                    checked={group.isFieldGroup}
                                    onChange={(event) => {
                                        const value = event.target.checked;
                                        this.setState((state) => (state.group.isFieldGroup = value, state));
                                    }}
                                    labelText="This is a field personnel group"
                                />
                                <FormCheckbox
                                    className="mt-2 mb-2"
                                    id="isImpersonatedGroup"
                                    checked={group.isImpersonatedGroup}
                                    onChange={(event) => {
                                        const value = event.target.checked;
                                        this.setState((state) => (state.group.isImpersonatedGroup = value, state));
                                    }}
                                    labelText="Members of this group can be impersonated"
                                />
                                <FormCheckbox
                                    className="mt-2 mb-2"
                                    id="isExcludedFromBillingReport"
                                    checked={group.isExcludedFromBillingReport}
                                    onChange={(event) => {
                                        const value = event.target.checked;
                                        this.setState((state) => (state.group.isExcludedFromBillingReport = value, state));
                                    }}
                                    labelText="Exclude this group from the payroll billing report"
                                />
                                <FormCheckbox
                                    className="mt-2 mb-2"
                                    id="requireMFA"
                                    checked={group.requireMFA}
                                    onChange={(event) => {
                                        const value = event.target.checked;
                                        this.setState((state) => (state.group.requireMFA = value, state));
                                    }}
                                    labelText="Multi-factor authentication"
                                />
                            </FormGroup>
                            {
                                !adminOrRegSpptSelected
                                && (
                                    <FormGroup>
                                        <FormCheckbox
                                            className="mt-2 mb-2"
                                            id="expandDispatching"
                                            checked={group?.expandDispatching ?? false}
                                            onChange={(event) => {
                                                const value = event.target.checked;
                                                this.setState((state) => (state.group.expandDispatching = value, state));
                                            }}
                                            labelText="Allow Expanded Dispatching Assignments"
                                        />
                                    </FormGroup>
                                )
                            }
                            {/* { */}
                            {/*    !adminOrRegSpptSelected && !!group.expandDispatching && */}
                            {/*    <FormGroup> */}
                            {/*        <FormLabel */}
                            {/*            htmlFor="dispatchCompanies" */}
                            {/*            label="Expanded Dispatching" */}
                            {/*            required={false} /> */}
                            {/*        <ValidatedSelect */}
                            {/*            id="dispatchCompanies" */}
                            {/*            name="group.expandedDispatchCompanies" */}
                            {/*            options={dispatchCompanies} */}
                            {/*            isMulti */}
                            {/*            required={false} */}
                            {/*            value={selectedDispatchCompanies ?? ''} */}
                            {/*            onChange={this.onSelectChanged} */}
                            {/*            error="A Dispatching selection is required when expanding dispatching assignments." */}
                            {/*        /> */}
                            {/*        <FlexEndRow className="w-100 pt-2 pl-2"> */}
                            {/*            <small className="site-link mr-2" onClick={this.addAllDispatching}>Add All</small> */}
                            {/*            // */}
                            {/*            <small className="site-link ml-2" onClick={this.removeAllDispatching}>Remove All</small> */}
                            {/*        </FlexEndRow> */}
                            {/*    </FormGroup> */}
                            {/* } */}
                        </Col>
                    </Row>
                    <FormDivider />

                    <FlexCenterRow className="mb-3">
                        <SaveButton
                            saving={!!this.state.saving}
                            label={!existing ? 'Save New Group' : 'Save'}
                        />
                    </FlexCenterRow>

                </AppPageForm>
            </>
        );
    }
}
export default GroupForm;
